import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Icon, { type IconProps } from '@cloudscape-design/components/icon';
import Tabs from '@cloudscape-design/components/tabs';
import { CountriesTab } from '@/features/countries/CountriesTab';
import { LanguagesTab } from '@/features/countries/LanguagesTab';

type Tab = 'countries' | 'languages';

/** Rótulo de aba com ícone à esquerda. */
function tabLabel(icon: IconProps.Name, text: string): React.ReactNode {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Icon name={icon} />
      {text}
    </span>
  );
}

/**
 * Tela `/countries` — hub de configuração por país, em abas: **Países**
 * (os 225 locais da Google Play, configurados ou não, `CountriesTab`) e
 * **Idiomas** (matriz idioma × plataforma, só leitura, `LanguagesTab`).
 *
 * Origem: sem issue — commit a4bf785ee, 2026-06-11 (tela Países com abas).
 *
 * Usado por: rota `countries` (`routes/router.tsx`), menu Sistema
 * (`AdminLayout`). Spec: `docs/screens/countries-admin.md`.
 *
 * Arquitetura: a leitura e a escrita do catálogo de países moram na
 * `CountriesTab` (`features/countries/`); esta tela só organiza as abas.
 *
 * Histórico:
 *  - 2026-09-25 — (#744) a aba SMS saiu com o fim do OTP (`c7755754d`).
 *  - 2026-10-01 — (#756) a aba PSP saiu para `/psp`; todo setup de PSP fica lá.
 *  - 2026-10-10 13:39 +0200 — (#926) a aba Países dizia "a implementar",
 *    mas existe desde 2026-06-11 (`a0529e97c`, CRUD por continente).
 *  - 2026-10-13 12:05 +0200 — (#957) a aba Idiomas sai do inline para
 *    `LanguagesTab` (matriz por plataforma, do manifesto `coverage.json`).
 */
export function CountriesPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('countries');

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={t('admin.countries.subtitle')}>
          {t('admin.countries.title')}
        </Header>
      }
    >
      <Tabs
        activeTabId={tab}
        onChange={({ detail }): void => {
          setTab(detail.activeTabId as Tab);
        }}
        tabs={[
          {
            id: 'countries',
            label: tabLabel('map', t('admin.countries.tabs.countries')),
            content: <CountriesTab />,
          },
          {
            id: 'languages',
            label: tabLabel('globe', t('admin.countries.tabs.languages')),
            content: <LanguagesTab />,
          },
        ]}
      />
    </ContentLayout>
  );
}
